import React, { useEffect } from "react";
import { Form, Tooltip, Modal, Input, Button, Select, Row, Col, Divider } from "antd";
import { MinusCircleOutlined, PlusCircleFilled } from "@ant-design/icons";
import { MaskedInput } from "antd-mask-input";
import { useRouteMatch } from "react-router-dom";
import { AccountCreateActiveModal } from "../types/AccountCreateActiveModal";
import { CreateAccountData } from "../types/CreateAccountData";
import { ContactIndividualCreate } from "../types/ContactIndividualCreate";
import { SetActiveModal } from "../../../lib/utils/useActiveModal";
import { AnyObject } from "../../../lib/interfaces/AnyObject";
import { getSelectOptions, selectFilterOption } from "../../../lib/utils";
import { sexDictionary } from "../../../lib/const/sexDictionary";
import "./ContactIndividualModal.less";
import usePhoneMask from "../../../lib/hooks/usePhoneMask";
import { useAppSelector } from "../../..";

interface ContactIndividualModalProps {
  visible: boolean;
  setActiveModal: SetActiveModal<AccountCreateActiveModal>;
  contactIndividualFormData?: CreateAccountData["contactIndividual"];
  onSubmit: (user: ContactIndividualCreate) => void;
  activeModalOptions?: AnyObject;
}

const labelSize = 6;
const wrapperSize = 18;

const formItemLayout = {
  labelCol: {
    span: labelSize,
  },
  wrapperCol: {
    span: wrapperSize,
  },
};

const formItemLayoutWithoutLabel = {
  wrapperCol: {
    offset: labelSize,
    span: wrapperSize,
  },
};

export const ContactIndividualModal = (props: ContactIndividualModalProps) => {
  const {
    visible,
    setActiveModal,
    contactIndividualFormData,
    onSubmit,
    activeModalOptions,
  } = props;

  const { phoneMask, cleanStringNumber } = usePhoneMask();
  const { phoneTypes, regions, countries } = useAppSelector((state) => state.dicts);

  const [form] = Form.useForm();
  const { url } = useRouteMatch();

  const createInitialValues: ContactIndividualCreate = {
    lastName: "Проворов",
    firstName: "Иван",
    middleName: "Пашотович",
    sex: "M",
    position: "Лекарь",
    emails: ["qwerty@mail.ru"],
    phones: [
      {
        countryCode: "7",
        phone: "9879212121",
        phoneTypeId: 1,
      },
    ],
    regionId: 1,
    countryId: 255,
    unstructuredAddress: "Случайный адрес",
  };

  const initialValues = {
    lastName: undefined,
    firstName: undefined,
    middleName: undefined,
    sex: undefined,
    position: undefined,
    emails: [""],
    phones: [{ countryCode: "", phone: "", phoneTypeId: 1 }],
    regionId: undefined,
    countryId: undefined,
    unstructuredAddress: undefined,
  };

  useEffect(() => {
    if (!url.includes("contacts")) {
      form.setFieldsValue(activeModalOptions || createInitialValues);
    }
  }, [activeModalOptions, createInitialValues]);

  const onCancel = () => {
    form.resetFields();
    setActiveModal("");
  };

  const footer = (
    <div style={{ display: "flex", justifyContent: "center" }}>
      <Button style={{ marginRight: "20px", width: "100px" }} onClick={onCancel}>
        Отмена
      </Button>
      <Button type="primary" style={{ width: "100px" }} onClick={form.submit}>
        Сохранить
      </Button>
    </div>
  );

  const onFinish = (contactIndividual) => {
    // console.log(contactIndividual);
    onSubmit(contactIndividual);
    onCancel();
  };

  return (
    <Modal
      visible={visible}
      footer={footer}
      onCancel={onCancel}
      title={<h2 style={{ color: "#58647C" }}>Добавление контактного лица</h2>}
      width="900px"
      className="contact-individual-modal"
    >
      <Form form={form} onFinish={onFinish} labelAlign="left" initialValues={initialValues}>
        <h3>Личные данные</h3>
        <Form.Item
          label="Фамилия"
          name="lastName"
          className="form-item-required__hidden-error"
          rules={[{ required: true }]}
          {...formItemLayout}
        >
          <Input />
        </Form.Item>
        <Form.Item
          label="Имя"
          name="firstName"
          className="form-item-required__hidden-error"
          rules={[{ required: true }]}
          {...formItemLayout}
        >
          <Input />
        </Form.Item>
        <Form.Item label="Отчество" name="middleName" {...formItemLayout}>
          <Input />
        </Form.Item>
        <Form.Item
          label="Пол"
          name="sex"
          className="form-item-required__hidden-error"
          rules={[{ required: true }]}
          {...formItemLayout}
        >
          <Select
            options={getSelectOptions({
              list: sexDictionary,
            })}
          />
        </Form.Item>
        <Divider />
        <h3>Должность</h3>
        <Form.Item label="Название должности" name="position" {...formItemLayout}>
          <Input />
        </Form.Item>
        <Divider />
        <Form.List name="emails">
          {(fields, { add, remove }) => (
            <>
              <h3>
                Электронная почта
                <Tooltip title="Добавить электронную почту">
                  <PlusCircleFilled className="plus-icon" onClick={() => add()} />
                </Tooltip>
              </h3>
              {fields.map((field, index) => (
                <div key={field.key} style={{ display: "flex" }}>
                  <Form.Item
                    {...field}
                    {...(index === 0 ? formItemLayout : formItemLayoutWithoutLabel)}
                    style={{ width: "100%" }}
                    label={index === 0 ? "Email" : ""}
                    rules={[
                      {
                        required: true,
                        whitespace: true,
                        type: "email",
                        message: "Введите корректный e-mail",
                      },
                    ]}
                  >
                    <Input />
                  </Form.Item>
                  {fields.length > 1 && (
                    <MinusCircleOutlined
                      className="delete-email"
                      onClick={() => remove(field.name)}
                    />
                  )}
                </div>
              ))}
            </>
          )}
        </Form.List>
        <Divider />
        <Form.List name="phones">
          {(fields, { add, remove }) => {
            return (
              <>
                <Row gutter={20}>
                  <Col>
                    <h3>
                      Номер телефона
                      <Tooltip title="Добавить номер телефона">
                        <PlusCircleFilled className="plus-icon" onClick={() => add()} />
                      </Tooltip>
                    </h3>
                  </Col>
                </Row>

                {fields.length > 0 && (
                  <Row gutter={10}>
                    <Col offset={labelSize} span={4}>
                      <div className="phone-title">Код страны</div>
                    </Col>
                    <Col span={8}>
                      <div className="phone-title">Номер</div>
                    </Col>
                    <Col span={6}>
                      <div className="phone-title">Тип номера</div>
                    </Col>
                  </Row>
                )}

                {fields.map(({ name, key, ...field }, index) => (
                  <Row key={key} gutter={20}>
                    <Col span={4} offset={labelSize}>
                      <Form.Item
                        {...field}
                        name={[name, "countryCode"]}
                        className="form-item-required__hidden-error"
                        rules={[{ required: true, message: "Поле обязательно для заполнения" }]}
                      >
                        <MaskedInput mask={phoneMask.country} name="countryCode" allowClear />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item
                        {...field}
                        name={[name, "phone"]}
                        className="form-item-required__hidden-error"
                        rules={[{ required: true, message: "Поле обязательно для заполнения" }]}
                      >
                        <MaskedInput mask={phoneMask.phone} name="phone" allowClear />
                      </Form.Item>
                    </Col>
                    <Col span={6}>
                      <Form.Item
                        initialValue={1}
                        {...field}
                        name={[name, "phoneTypeId"]}
                        className="form-item-required__hidden-error"
                      >
                        <Select>
                          {phoneTypes.map((phoneType) => (
                            <Select.Option value={phoneType.id} key={phoneType.id}>
                              {phoneType.name}
                            </Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                    {fields.length > 1 && (
                      <MinusCircleOutlined className="delete-phone" onClick={() => remove(name)} />
                    )}
                  </Row>
                ))}
              </>
            );
          }}
        </Form.List>
        <Divider />
        <h3>Фактический адрес</h3>
        <Form.Item
          label="Регион"
          name="regionId"
          rules={[
            {
              required: true,
            },
          ]}
          {...formItemLayout}
          className="form-item-required__hidden-error"
        >
          <Select
            onChange={() => {
              form.resetFields(["countryId"]);
            }}
            showSearch
            filterOption={selectFilterOption}
            options={getSelectOptions({
              list: regions,
            })}
          />
        </Form.Item>
        <Form.Item dependencies={["regionId"]}>
          {({ getFieldValue }) => {
            const regionId = getFieldValue("regionId");
            return (
              <Form.Item
                name="countryId"
                className="form-item-required__hidden-error__mb0"
                label="Страна"
                rules={[{ required: true }]}
                {...formItemLayout}
              >
                <Select
                  disabled={regionId === undefined}
                  options={getSelectOptions({
                    list: countries.filter(({ parentDicId }) => parentDicId === regionId),
                  })}
                  placeholder={regionId === undefined ? "Необходимо выбрать регион" : ""}
                  showSearch
                  filterOption={selectFilterOption}
                />
              </Form.Item>
            );
          }}
        </Form.Item>
        <Form.Item
          label="Адрес"
          name="unstructuredAddress"
          {...formItemLayout}
          className="form-item-required__hidden-error__mb0"
        >
          <Input.TextArea placeholder="Введите адрес в свободной форме" />
        </Form.Item>
      </Form>
    </Modal>
  );
};
